import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Warning: `twMerge` can only deduplicate classes whose group it knows. Our own
 * tokens (`rounded-panel`, `shadow-raised`, `bg-surface`, `bg-app`…) are
 * unknown to it by default: without this registration,
 * `cn('rounded-panel', 'rounded-full')` keeps BOTH and the winner depends on
 * stylesheet order — a silent style bug, typically found months later.
 *
 * Every value added to `styles/theme.css` must be declared here.
 * `cn.test.ts` locks the behaviour.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      radius: ['tag', 'badge', 'button', 'panel', 'card', 'thumbnail'],
      shadow: ['button', 'raised', 'float'],
      font: ['display'],
      text: ['4xs', '3xs', '2xs', 'md'],
      color: [
        'backdrop',
        'canvas-top',
        'canvas-bottom',
        'nav',
        'surface',
        'surface-strong',
        'surface-hover',
        'border',
        'fg',
        'fg-muted',
        'fg-subtle',
        'accent',
        'accent-dark',
        'accent-strong',
        'accent-bright',
        'on-accent',
        'accent-soft',
        'accent-violet',
        'violet-fg',
        'danger',
        'success',
        'gold',
        'gold-deep',
        'gold-fg',
        'glass',
        'silver',
        'level-1',
        'level-2',
        'level-3',
        'level-4',
        'level-5',
      ],
    },
    classGroups: {
      // `bg-app` and `bg-app-top` are our own `@utility` (the background
      // gradient, and its first screenful for anything stuck to the top of the
      // viewport): they must conflict with the other `bg-*` to stay
      // replaceable.
      'bg-color': ['bg-app', 'bg-app-top'],
    },
  },
});

/**
 * Merges conditional classes, then resolves Tailwind conflicts.
 *
 * The single exit point for classes in the whole application.
 *
 * @param inputs Class values, conditionals included.
 * @returns The merged class string.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
